import { AlertCircle,AlertTriangle, Info } from 'lucide-react'
import React from 'react'

import { Button } from './ui/button'
import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/Dialog'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  message: React.ReactNode
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'destructive' | 'warning'
}

const variantConfig = {
  default: {
    icon: Info,
    iconColor: 'text-blue-600',
    confirmButton: 'default',
  },
  destructive: {
    icon: AlertCircle,
    iconColor: 'text-red-600',
    confirmButton: 'destructive',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-yellow-600',
    confirmButton: 'default',
  },
} as const

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
}: ConfirmDialogProps) {
  const config = variantConfig[variant]
  const Icon = config.icon

  const [submitting, setSubmitting] = React.useState(false)

  const handleConfirm = async () => {
    try {
      setSubmitting(true)
      const result = onConfirm()
      if (result && typeof (result as any).then === 'function') {
        await (result as any)
      }
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} ariaLabel={title}>
      <DialogHeader>
        <div className="flex items-start gap-4">
          <div className={`${config.iconColor}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{message}</DialogDescription>
          </div>
        </div>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
          {cancelText}
        </Button>
        <Button
          variant={config.confirmButton as 'default' | 'destructive'}
          size="sm"
          disabled={submitting}
          onClick={handleConfirm}
        >
          {submitting ? 'Working…' : confirmText}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default ConfirmDialog


